import router from 'next/router'
import { useState, VFC } from 'react'
import {
  createCompanyMessageRoomPinFetcher,
  deleteCompanyMessageRoomPinFetcher,
} from 'src/api/companyMessageRoomPin'
import { PinIcon } from 'src/assets/svgs'
import { useMessageModal } from 'src/hooks/useModal'
import { Room, RoomUniqueId } from 'src/models/Room'
import {
  _messagesecondary,
  _unreadbatchmessage,
  accentColor,
} from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import { convertMessagePageQuqry, toMessagePageQuery } from 'src/utils/routes'
import styled from 'styled-components'
import { RoomItem } from './RoomItem'

export { DeletedUserModalContent, RoomsComponent }

const DeletedUserModalContent = {
  message: 'このユーザーは退会済みです。',
  buttonLabel: '閉じる',
  backgroundColor: accentColor,
}

type RoomsProps = {
  rooms: Room[]
  FecthMoreTrigger: VFC
  currentRoomUniqueId: RoomUniqueId | undefined
  showDeleted: boolean
  setHidingRoom: (room: Room) => void
  markAsUnread: (room: Room) => void
  hasPaid: boolean
  unreadBatchMessageRoom?: boolean
}

type RoomListsProps = Omit<RoomsProps, 'FecthMoreTrigger'>
const RoomLists: VFC<RoomListsProps> = ({
  showDeleted,
  rooms,
  currentRoomUniqueId,
  setHidingRoom,
  markAsUnread,
  hasPaid,
  unreadBatchMessageRoom,
}) => {
  const { open: openMessageModal } = useMessageModal()

  const pin = async (room: Room) => {
    const res = await createCompanyMessageRoomPinFetcher(room.id, room.isGroup)
    if (res.error) {
      openMessageModal({ message: res.error.errorMessages?.join('\n') })
      return
    }
    if (res.success) router.reload()
  }

  const unpin = async (room: Room) => {
    const res = await deleteCompanyMessageRoomPinFetcher(room.id, room.isGroup)
    if (res.error) {
      openMessageModal({ message: res.error.errorMessages?.join('\n') })
      return
    }
    if (res.success) router.reload()
  }

  const openRoom = (room: Room) => {
    if (room.hasDeletedUser) openMessageModal(DeletedUserModalContent)

    const query = convertMessagePageQuqry(router.query)
    !showDeleted &&
      router.push(
        toMessagePageQuery({
          pathname: router.pathname,
          query: {
            ...query,
            room_id: room.uniqueId,
          },
        }),
      )
  }
  const [TagsIndex, setTagsIndex] = useState<number | undefined>(undefined)
  const onClickTagsManagement = (index: number) => {
    setTagsIndex(index)
  }
  const close = () => {
    setTagsIndex(undefined)
  }

  const getActions = (room: Room, index: number) => {
    const actions = [
      showDeleted
        ? {
            label: '復元',
            onClick: () => setHidingRoom(room),
          }
        : {
            label: '未読に戻す',
            onClick: () => markAsUnread(room),
          },
      room.pinned
        ? {
            label: 'ピン留めを解除する',
            onClick: () => unpin(room),
          }
        : {
            label: 'ルームをピン留めする',
            onClick: () => pin(room),
          },
    ]
    // NOTE: 有料会員のみタグ管理アクションを追加
    if (hasPaid) {
      actions.push({
        label: 'タグ管理',
        onClick: () => onClickTagsManagement(index),
      })
    }
    return actions
  }

  return (
    <>
      {rooms.map((room, index) => {
        const actions = getActions(room, index)
        return (
          <li key={room.uniqueId}>
            <RoomItem
              openRoom={() => openRoom(room)}
              counters={room.counters}
              latestMessage={room.latestMessage}
              hasRead={room.hasRead}
              isRequestReceived={room.isRequestReceived}
              hidden={room.isHidden}
              isActive={currentRoomUniqueId === room.uniqueId}
              actions={actions}
              hideRoom={() => setHidingRoom(room)}
              displayTagsManagement={index === TagsIndex}
              room={room}
              close={close}
              unreadBatchMessageRoom={unreadBatchMessageRoom}
            />
          </li>
        )
      })}
    </>
  )
}

const RoomsContent: VFC<RoomsProps & { className?: string }> = ({
  className,
  showDeleted,
  rooms,
  currentRoomUniqueId,
  FecthMoreTrigger,
  setHidingRoom,
  markAsUnread,
  hasPaid,
}) => {
  const unreadBatchMessageRooms = rooms.filter(
    (room) => room.unreadBatchMessageRoom,
  )
  const pinnedRooms = rooms.filter((room) => room.pinned)
  const messageRooms = rooms
    .filter((room) => !room.pinned)
    .filter((room) => !room.unreadBatchMessageRoom)
  return (
    <ScrollableList className={className}>
      {!!unreadBatchMessageRooms.length && (
        <>
          <TitleWrapperDiv>
            <UnreadBatchMessageRoomTitle>
              運営よりお知らせ
            </UnreadBatchMessageRoomTitle>
          </TitleWrapperDiv>
          <RoomLists
            rooms={unreadBatchMessageRooms}
            currentRoomUniqueId={currentRoomUniqueId}
            showDeleted={showDeleted}
            markAsUnread={markAsUnread}
            setHidingRoom={setHidingRoom}
            hasPaid={hasPaid}
            unreadBatchMessageRoom={true}
          />
        </>
      )}
      {!!pinnedRooms.length && (
        <>
          <TitleWrapperDiv>
            <PinIcon height={16} width={16} />
            <PinnedRoomTitle>ピン留め中</PinnedRoomTitle>
          </TitleWrapperDiv>
          <RoomLists
            rooms={pinnedRooms}
            currentRoomUniqueId={currentRoomUniqueId}
            showDeleted={showDeleted}
            markAsUnread={markAsUnread}
            setHidingRoom={setHidingRoom}
            hasPaid={hasPaid}
          />
        </>
      )}
      {!!messageRooms.length && (
        <>
          <AllRoomTitle>全てのメッセージ</AllRoomTitle>
          <RoomLists
            rooms={messageRooms}
            currentRoomUniqueId={currentRoomUniqueId}
            showDeleted={showDeleted}
            markAsUnread={markAsUnread}
            setHidingRoom={setHidingRoom}
            hasPaid={hasPaid}
          />
        </>
      )}
      <FecthMoreTrigger />
    </ScrollableList>
  )
}

const RoomsComponent = styled(RoomsContent)``

const ScrollableList = styled.ul`
  flex: 1;
  overflow-y: auto;
  padding: 0 4px 16px 4px;
  height: 200px;
  li {
    margin-top: 8px;
  }
`

const TitleWrapperDiv = styled.div`
  display: flex;
  padding-top: 8px;
  padding-left: 8px;
  background-color: transparent;
  font-weight: ${fontWeightBold};
`

const UnreadBatchMessageRoomTitle = styled.p`
  color: ${_unreadbatchmessage};
  padding-left: 4px;
  height: 16px;
  line-height: 16px;
`

const PinnedRoomTitle = styled.p`
  color: ${_messagesecondary};
  padding-left: 4px;
  height: 16px;
  line-height: 16px;
`

const AllRoomTitle = styled.p`
  color: ${_messagesecondary};
  padding-top: 16px;
  padding-left: 8px;
`
